import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/apiClient';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Target, 
  Megaphone, 
  Lightbulb, 
  GitBranch, 
  Video, 
  Film,
  Building2,
  ChevronDown,
  CheckCircle2,
  Sparkles,
  TrendingUp,
  Users,
  FileText,
  Zap
} from 'lucide-react';
import { toast } from 'sonner';

const HomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeBusiness, setActiveBusiness] = useState(null);
  const [activeICP, setActiveICP] = useState(null);
  const [allBusinesses, setAllBusinesses] = useState([]);
  const [showBusinessDropdown, setShowBusinessDropdown] = useState(false);
  const [contentStats, setContentStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (activeBusiness?.id) {
      loadContentStats(activeBusiness.id);
    }
  }, [activeBusiness]);

  const loadData = async () => {
    try {
      const [activeResponse, allResponse] = await Promise.all([
        apiClient.get('/user/active-business'),
        apiClient.get('/businesses')
      ]);
      setActiveBusiness(activeResponse.data.business);
      setActiveICP(activeResponse.data.icp);
      setAllBusinesses(allResponse.data);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const loadContentStats = async (businessId) => {
    try {
      setLoadingStats(true);
      const response = await apiClient.get(`/businesses/${businessId}/content-summary`);
      setContentStats(response.data);
    } catch (error) {
      console.error('Error loading content stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const handleChangeActiveBusiness = async (businessId) => {
    try {
      const response = await apiClient.put(`/businesses/${businessId}/set-active`);
      setActiveBusiness(response.data.business);
      setActiveICP(response.data.icp);
      setShowBusinessDropdown(false);
      toast.success('Empresa activa actualizada');
      loadContentStats(businessId);
    } catch (error) {
      console.error('Error changing active business:', error);
      toast.error('Error al cambiar empresa activa');
    }
  };

  const quickActions = [
    { 
      title: 'Crear ICP', 
      description: 'Define tu cliente ideal',
      icon: Target, 
      path: '/icp', 
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-50'
    },
    { 
      title: 'Nueva Campaña', 
      description: 'Estrategia + Anuncios + CTAs',
      icon: Megaphone, 
      path: '/campaign-generator', 
      color: 'from-purple-500 to-pink-500',
      bgColor: 'bg-purple-50'
    },
    { 
      title: 'Generar Contenido', 
      description: 'Posts y planificación 3-3-3',
      icon: Lightbulb, 
      path: '/content-generator', 
      color: 'from-orange-500 to-red-500',
      bgColor: 'bg-orange-50'
    },
    { 
      title: 'Crear Embudo', 
      description: 'TOF-MOF-BOF completo',
      icon: GitBranch, 
      path: '/funnel-builder', 
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-50'
    },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Welcome Header */}
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
          ¡Hola, {user?.name?.split(' ')[0] || 'Usuario'}! 👋
        </h1>
        <p className="text-slate-600 mt-1">Tu Director de Marketing AI está listo para trabajar</p>
      </div>

      {/* Business Selector Card */}
      {activeBusiness && (
        <Card className="mb-6 border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-blue-50">
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-xs text-purple-600 font-medium uppercase tracking-wide">Empresa Activa</p>
                  <div className="relative">
                    <button
                      onClick={() => setShowBusinessDropdown(!showBusinessDropdown)}
                      className="flex items-center gap-2 text-lg font-semibold text-slate-900 hover:text-purple-700 transition-colors"
                    >
                      {activeBusiness.business_name || activeBusiness.business_description?.substring(0, 30) + '...'}
                      <ChevronDown className={`w-4 h-4 transition-transform ${showBusinessDropdown ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {showBusinessDropdown && (
                      <>
                        <div className="fixed inset-0 z-30" onClick={() => setShowBusinessDropdown(false)} />
                        <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-lg border border-slate-200 shadow-xl z-40 max-h-64 overflow-y-auto">
                          {allBusinesses.map((business) => (
                            <button
                              key={business.id}
                              onClick={() => handleChangeActiveBusiness(business.id)}
                              className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-purple-50 transition-colors text-left ${
                                business.id === activeBusiness.id ? 'bg-purple-50' : ''
                              }`}
                            >
                              <Building2 className="w-4 h-4 text-purple-500" />
                              <span className="text-sm text-slate-700 flex-1 truncate">
                                {business.business_name || business.business_description?.substring(0, 35) + '...'}
                              </span>
                              {business.id === activeBusiness.id && (
                                <CheckCircle2 className="w-4 h-4 text-purple-600" />
                              )}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              {activeICP && (
                <div className="flex items-center gap-2 px-4 py-2 bg-white/70 rounded-lg">
                  <Target className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-slate-700">
                    ICP: <span className="font-medium">{activeICP.demographics?.age_range || 'Configurado'}</span>
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      {contentStats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8">
          <Card className="border-0 shadow-sm bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Megaphone className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{contentStats.statistics.campaigns}</p>
                  <p className="text-xs text-slate-500">Campañas</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{contentStats.statistics.ads}</p>
                  <p className="text-xs text-slate-500">Anuncios</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Lightbulb className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{contentStats.statistics.content}</p>
                  <p className="text-xs text-slate-500">Contenidos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{contentStats.total_items}</p>
                  <p className="text-xs text-slate-500">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Quick Actions */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Zap className="w-5 h-5 text-yellow-500" />
          Acciones Rápidas
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions.map((action, index) => {
            const Icon = action.icon;
            return (
              <Card 
                key={index}
                className="border-0 shadow-sm hover:shadow-md transition-all cursor-pointer group"
                onClick={() => navigate(action.path)}
              >
                <CardContent className="p-4">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${action.color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">{action.title}</h3>
                  <p className="text-xs text-slate-500">{action.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* All Modules */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-purple-500" />
          Todos los Módulos
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { title: 'Arquitecto de ICP', desc: 'Perfiles completos de cliente ideal con análisis psicológico', icon: Target, path: '/icp', color: 'from-blue-500 to-cyan-500' },
            { title: 'Generador de Campañas', desc: 'Estrategia + Anuncios + CTAs en un paquete completo', icon: Megaphone, path: '/campaign-generator', color: 'from-purple-500 to-pink-500', badge: '3 en 1' },
            { title: 'Generador de Contenido', desc: 'Posts individuales o planificación mensual (27 piezas)', icon: Lightbulb, path: '/content-generator', color: 'from-orange-500 to-red-500', badge: '3-3-3' },
            { title: 'Generador de Scripts', desc: 'Scripts profesionales para TikTok, Reels, YouTube', icon: Video, path: '/script-generator', color: 'from-red-500 to-pink-500' },
            { title: 'Constructor de Embudos', desc: 'Embudos completos de publicidad y ventas', icon: GitBranch, path: '/funnel-builder', color: 'from-green-500 to-emerald-500' },
            { title: 'MÓNICA - Video Creativo', desc: 'Directora creativa AI para videos cinematográficos', icon: Film, path: '/monica', color: 'from-pink-500 to-rose-500', badge: 'Pro' },
          ].map((module, index) => {
            const Icon = module.icon;
            return (
              <Card 
                key={index}
                className="border-0 shadow-sm hover:shadow-lg transition-all cursor-pointer group"
                onClick={() => navigate(module.path)}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${module.color} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    {module.badge && (
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                        {module.badge}
                      </span>
                    )}
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">{module.title}</h3>
                  <p className="text-sm text-slate-500 line-clamp-2">{module.desc}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default HomePage;
